/**
 * Leave panel on the Staff Profile page.
 *
 * Reuses the existing /api/leave endpoints so nothing here duplicates state
 * with the main Leave & Holidays screen. Only Admin/Manager can edit fields;
 * everyone with access to the profile sees the balance and history.
 */
import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Umbrella, Plus, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

export default function StaffLeavePanel({ staffUserId }) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entOpen, setEntOpen] = useState(false);
  const [entForm, setEntForm] = useState({});
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ adjustment_type: "correction", amount_days: 0, reason: "", effective_date: new Date().toISOString().slice(0, 10) });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r, a, s, pt] = await Promise.all([
        api.get("/leave/balance", { params: { staff_user_id: staffUserId } }),
        api.get("/leave/requests", { params: { staff_user_id: staffUserId } }),
        api.get("/leave/adjustments", { params: { staff_user_id: staffUserId } }),
        api.get("/leave/carry-over/snapshots", { params: { staff_user_id: staffUserId } }).catch(() => ({ data: [] })),
        api.get("/leave/patterns").catch(() => ({ data: [] })),
      ]);
      setBalance(b.data);
      setRequests(r.data || []);
      setAdjustments(a.data || []);
      setSnapshots(s.data || []);
      setPatterns(pt.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [staffUserId]);
  useEffect(() => { load(); }, [load]);

  const openEnt = () => {
    setEntForm({
      entitlement_unit: balance.unit,
      annual_entitlement_days: balance.unit === "days" ? balance.annual_entitlement : "",
      annual_entitlement_hours: balance.unit === "hours" ? balance.annual_entitlement : "",
      carried_over_days: balance.unit === "days" ? balance.carried_over : "",
      carried_over_hours: balance.unit === "hours" ? balance.carried_over : "",
      working_pattern: balance.working_pattern || DAY_KEYS.reduce((o, k) => ({ ...o, [k]: k === "sat" || k === "sun" ? 0 : 8 }), {}),
    });
    setEntOpen(true);
  };

  const saveEnt = async () => {
    try {
      const payload = { ...entForm };
      // Only send the numeric fields relevant to the chosen unit
      if (entForm.entitlement_unit === "days") {
        delete payload.annual_entitlement_hours; delete payload.carried_over_hours;
      } else {
        delete payload.annual_entitlement_days; delete payload.carried_over_days;
      }
      // Coerce to numbers
      for (const k of ["annual_entitlement_days", "annual_entitlement_hours", "carried_over_days", "carried_over_hours"]) {
        if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]);
      }
      await api.put(`/leave/entitlement/${staffUserId}`, payload);
      toast.success("Entitlement updated");
      setEntOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveAdj = async () => {
    if (!adjForm.reason.trim()) { toast.error("A reason is required for every manual adjustment"); return; }
    const isHours = balance.unit === "hours";
    const payload = {
      staff_user_id: staffUserId,
      adjustment_type: adjForm.adjustment_type,
      reason: adjForm.reason.trim(),
      effective_date: adjForm.effective_date,
      [isHours ? "amount_hours" : "amount_days"]: Number(adjForm.amount_days),
    };
    try {
      await api.post("/leave/adjustments", payload);
      toast.success(`Adjustment recorded (${payload[isHours ? "amount_hours" : "amount_days"]} ${balance.unit})`);
      setAdjOpen(false);
      setAdjForm({ adjustment_type: "correction", amount_days: 0, reason: "", effective_date: new Date().toISOString().slice(0, 10) });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !balance) {
    return <div className="bg-white border border-slate-200 rounded-md p-6 text-sm text-slate-500" data-testid="staff-leave-loading">Loading leave…</div>;
  }

  const progressPct = balance.total_entitlement > 0
    ? Math.min(100, Math.round(((balance.taken + balance.booked) / balance.total_entitlement) * 100))
    : 0;

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="staff-leave-panel">
      <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">People</div>
          <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><Umbrella className="w-3.5 h-3.5 text-amber-600" /> Leave &amp; Holidays</h3>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={openEnt} data-testid="btn-edit-entitlement" className="h-8"><Pencil className="w-3 h-3 mr-1" /> Edit entitlement</Button>
            <Button size="sm" onClick={() => setAdjOpen(true)} data-testid="btn-add-adjustment" className="h-8 bg-slate-900 hover:bg-slate-800"><Plus className="w-3 h-3 mr-1" /> Manual adjustment</Button>
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div className="text-xs text-slate-500">Holiday year runs {balance.holiday_year_start} → {balance.holiday_year_end}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {[
            ["Entitlement", balance.annual_entitlement, "stat-sp-entitlement"],
            ["Carried over", balance.carried_over, "stat-sp-carried"],
            ["Adjustments", balance.adjustments, "stat-sp-adj"],
            ["Taken", balance.taken, "stat-sp-taken"],
            ["Booked", balance.booked, "stat-sp-booked"],
            ["Pending", balance.pending, "stat-sp-pending"],
            ["Available", balance.available, "stat-sp-available"],
          ].map(([label, value, testid], i) => (
            <div key={testid} className={`border p-2 ${i === 6 ? "bg-slate-900 text-white border-slate-900" : "bg-slate-50 border-slate-200"}`} data-testid={testid}>
              <div className={`text-lg font-heading font-semibold tabular-nums ${i === 6 ? "" : "text-slate-900"}`}>{fmt(value)}</div>
              <div className={`text-[10px] uppercase tracking-widest font-semibold ${i === 6 ? "text-slate-300" : "text-slate-500"}`}>{label} <span className="lowercase font-normal">· {balance.unit}</span></div>
            </div>
          ))}
        </div>
        <div>
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>{fmt(balance.taken + balance.booked)} of {fmt(balance.total_entitlement)} {balance.unit} taken/booked</span>
            <span className="font-medium">{progressPct}%</span>
          </div>
          <div className="mt-1 h-2 w-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-slate-900" style={{ width: `${progressPct}%` }} data-testid="staff-leave-progress" />
          </div>
        </div>

        {/* Adjustment history */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Adjustment history</div>
          {adjustments.length === 0 ? (
            <div className="text-xs text-slate-500">No manual adjustments recorded.</div>
          ) : (
            <table className="min-w-full text-xs" data-testid="adjustment-history">
              <thead className="bg-slate-50 text-slate-600 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="px-2 py-1.5 text-left">Effective</th>
                  <th className="px-2 py-1.5 text-left">Type</th>
                  <th className="px-2 py-1.5 text-right">Amount</th>
                  <th className="px-2 py-1.5 text-left">Reason</th>
                  <th className="px-2 py-1.5 text-left">By</th>
                  <th className="px-2 py-1.5 text-right">Balance after</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">{a.effective_date}</td>
                    <td className="px-2 py-1.5 capitalize">{(a.adjustment_type || "").replace(/_/g, " ")}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(a.amount_days ?? a.amount_hours)}</td>
                    <td className="px-2 py-1.5 text-slate-600">{a.reason}</td>
                    <td className="px-2 py-1.5">{a.created_by_name || a.created_by}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(a.new_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Year-close snapshots */}
        {snapshots.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Year-end snapshots</div>
            <table className="min-w-full text-xs" data-testid="snapshot-history">
              <thead className="bg-slate-50 text-slate-600 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="px-2 py-1.5 text-left">Year</th>
                  <th className="px-2 py-1.5 text-right">Entitlement</th>
                  <th className="px-2 py-1.5 text-right">Used</th>
                  <th className="px-2 py-1.5 text-right">Unused</th>
                  <th className="px-2 py-1.5 text-right">Carried forward</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">{s.year_start} → {s.year_end}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.total_entitlement)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.used)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.unused)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{fmt(s.carried_forward)}{s.capped && <span className="ml-1 text-amber-700">(capped)</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Leave history */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Leave history</div>
          {requests.length === 0 ? (
            <div className="text-xs text-slate-500">No leave requests on record.</div>
          ) : (
            <table className="min-w-full text-xs" data-testid="staff-leave-history">
              <thead className="bg-slate-50 text-slate-600 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="px-2 py-1.5 text-left">Leave type</th>
                  <th className="px-2 py-1.5 text-left">Start</th>
                  <th className="px-2 py-1.5 text-left">End</th>
                  <th className="px-2 py-1.5 text-right">{balance.unit === "hours" ? "Hours" : "Days"}</th>
                  <th className="px-2 py-1.5 text-left">Status</th>
                  <th className="px-2 py-1.5 text-left">Decided by</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 capitalize">{(r.leave_type || r.kind || "").replace(/_/g, " ")}</td>
                    <td className="px-2 py-1.5">{r.start_date}</td>
                    <td className="px-2 py-1.5">{r.end_date}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(balance.unit === "hours" ? r.hours : r.working_days)}</td>
                    <td className="px-2 py-1.5 capitalize">{(r.status || "").replace("_", " ")}</td>
                    <td className="px-2 py-1.5">{r.decided_by_name || r.decided_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Edit entitlement dialog */}
      <Dialog open={entOpen} onOpenChange={setEntOpen}>
        <DialogContent className="max-w-lg" data-testid="entitlement-dialog">
          <DialogHeader>
            <DialogTitle>Edit entitlement</DialogTitle>
            <DialogDescription>Changes here are logged in the Audit Centre.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Entitlement unit</Label>
              <Select value={entForm.entitlement_unit} onValueChange={(v) => setEntForm({ ...entForm, entitlement_unit: v })}>
                <SelectTrigger data-testid="ent-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="days">Days</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {entForm.entitlement_unit === "days" ? (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Annual (days)</Label><Input type="number" step="0.5" value={entForm.annual_entitlement_days ?? ""} onChange={(e) => setEntForm({ ...entForm, annual_entitlement_days: e.target.value })} data-testid="ent-annual-days" /></div>
                <div><Label>Carried over (days)</Label><Input type="number" step="0.5" value={entForm.carried_over_days ?? ""} onChange={(e) => setEntForm({ ...entForm, carried_over_days: e.target.value })} data-testid="ent-carry-days" /></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Annual (hours)</Label><Input type="number" step="0.5" value={entForm.annual_entitlement_hours ?? ""} onChange={(e) => setEntForm({ ...entForm, annual_entitlement_hours: e.target.value })} data-testid="ent-annual-hours" /></div>
                <div><Label>Carried over (hours)</Label><Input type="number" step="0.5" value={entForm.carried_over_hours ?? ""} onChange={(e) => setEntForm({ ...entForm, carried_over_hours: e.target.value })} data-testid="ent-carry-hours" /></div>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between">
                <Label>Working pattern (hours per day)</Label>
                <Select value="" onValueChange={(v) => {
                  const p = patterns.find((x) => x.id === v);
                  if (p) setEntForm({ ...entForm, working_pattern: { ...p.hours } });
                }}>
                  <SelectTrigger className="h-7 text-xs w-56" data-testid="ent-template-select"><SelectValue placeholder="Load template…" /></SelectTrigger>
                  <SelectContent>
                    {patterns.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-7 gap-1 mt-1" data-testid="ent-working-pattern">
                {DAY_KEYS.map((k) => (
                  <div key={k}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 text-center font-semibold">{DAY_LABELS[k]}</div>
                    <Input type="number" step="0.5" min="0" max="24"
                           className="h-8 text-center"
                           value={entForm.working_pattern?.[k] ?? 0}
                           onChange={(e) => setEntForm({ ...entForm, working_pattern: { ...(entForm.working_pattern || {}), [k]: Number(e.target.value) } })}
                           data-testid={`ent-pattern-${k}`} />
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Set a day to 0 if the employee is not contracted to work then. AYOBELL does not assume Monday–Friday.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntOpen(false)}>Cancel</Button>
            <Button onClick={saveEnt} data-testid="btn-save-entitlement" className="bg-slate-900 hover:bg-slate-800">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual adjustment dialog */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent data-testid="adjustment-dialog">
          <DialogHeader>
            <DialogTitle>Manual leave adjustment</DialogTitle>
            <DialogDescription>Add or subtract from the balance. A reason is required and captured in the Audit Centre.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={adjForm.adjustment_type} onValueChange={(v) => setAdjForm({ ...adjForm, adjustment_type: v })}>
                  <SelectTrigger data-testid="adj-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carry_over">Carry-over</SelectItem>
                    <SelectItem value="correction">Correction</SelectItem>
                    <SelectItem value="topup">Top-up</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount ({balance.unit})</Label>
                <Input type="number" step="0.5" value={adjForm.amount_days} onChange={(e) => setAdjForm({ ...adjForm, amount_days: e.target.value })} data-testid="adj-amount" />
              </div>
            </div>
            <div><Label>Effective date</Label><Input type="date" value={adjForm.effective_date} onChange={(e) => setAdjForm({ ...adjForm, effective_date: e.target.value })} data-testid="adj-date" /></div>
            <div><Label>Reason <span className="text-red-600">*</span></Label><Textarea rows={2} value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} data-testid="adj-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjOpen(false)}>Cancel</Button>
            <Button onClick={saveAdj} data-testid="btn-save-adjustment" className="bg-slate-900 hover:bg-slate-800">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
